import React from 'react';
import type {IconAsset} from '@sail/icons/types';
import {SelectField, SelectFieldItem, Icon, view, tokens} from '@sail/ui';
import {Text} from './Text';

interface SelectableItem {
  id: string;
  label: string;
  description?: React.ReactNode;
  icon?: IconAsset;
}

interface SelectProps<T extends SelectableItem> {
  items: T[];
  selectedItemId: string;
  onItemSelect: (itemId: string) => void;
  label?: string;
  placeholder?: string;
  size?: 'medium' | 'large';
  disabled?: boolean;
  disabledKeys?: Iterable<string>;
  variant?: 'default' | 'form';
}

const BUTTON_CSS = {
  default: {
    backgroundColor: 'offset',
    border: 'none',
    borderRadius: 'small',
    boxShadow: 'none',
    ':hover:not(:active):not([aria-disabled="true"])': {
      backgroundColor: tokens.color.neutral[50].toString(),
    },
    ':focus': {
      backgroundColor: tokens.color.neutral[50].toString(),
    },
    ':active:not([aria-disabled="true"])': {
      backgroundColor: tokens.color.neutral[50].toString(),
    },
  },
  form: {
    paddingX: '12px',
    paddingY: '8px',
    minHeight: '36px',
    height: '36px',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 400,
  },
} as const;

export const Select = <T extends SelectableItem>({
  items,
  selectedItemId,
  onItemSelect,
  label,
  placeholder,
  size = 'large',
  disabled,
  disabledKeys,
  variant = 'default',
}: SelectProps<T>) => {
  const subviews = {
    button: {
      css: {
        ...BUTTON_CSS[variant],
        opacity: disabled ? 0.5 : undefined,
      },
    },
  };

  return (
    <SelectField
      size={size}
      label={label}
      placeholder={placeholder}
      selectedKey={selectedItemId}
      hiddenElements={variant === 'form' ? undefined : ['label']}
      onSelectionChange={(value) => {
        onItemSelect(value as string);
      }}
      disabled={disabled}
      disabledKeys={disabledKeys}
      subviews={subviews}
    >
      {items.map((item) => (
        <SelectFieldItem
          id={item.id}
          key={item.id}
          icon={
            item.icon ? (
              <Icon icon={item.icon} css={{borderRadius: 'xsmall'}} />
            ) : undefined
          }
        >
          <view.div
            css={{
              stack: 'x',
              alignItems: 'center',
              distribute: 'space-between',
              width: 'fill',
            }}
          >
            <Text
              css={{color: 'inherit'}}
              type={variant === 'form' ? 'body' : 'bodyEmphasized'}
            >
              {item.label}
            </Text>
            {item.description ? item.description : null}
          </view.div>
        </SelectFieldItem>
      ))}
    </SelectField>
  );
};
